<template>
    <div class="flex flex-col items-center gap-4">
        <div class="flex">
            <input
                v-for="i in codeLength"
                :key="i - 1"
                :ref="(el) => setInputRef(i - 1, el)"
                :value="modelValue[i - 1] || ''"
                class="mx-1 block h-16 w-12 rounded-md border-2 border-neutral-600 bg-neutral-100 text-center text-5xl disabled:cursor-not-allowed disabled:opacity-40 dark:bg-neutral-800"
                inputmode="numeric"
                :type="inputType"
                maxlength="1"
                :disabled="disabled"
                @input="updateDigitAt(i - 1)"
                @keydown="handleKeyDown(i - 1, $event)"
                @paste="handlePaste"
            />
        </div>
        <pin-entry-keyboard :show-abort-button="showAbortButton" :disabled="disabled" class="w-96 max-w-screen-md" @cancel="cancel" @add-digit="addDigit" @remove-last-digit="removeLastDigit" />
    </div>
</template>

<script lang="ts" setup>
    import { nextTick, ref } from "vue";
    import PinEntryKeyboard from "@/components/PinEntryKeyboard.vue";

    const props = withDefaults(defineProps<{ modelValue: string; codeLength: number; inputType?: string; showAbortButton?: boolean; disabled?: boolean }>(), {
        inputType: "text",
        showAbortButton: true,
        disabled: false,
    });
    const emit = defineEmits<{ "update:modelValue": [value: string]; cancel: [] }>();

    const inputRefs = ref<(HTMLInputElement | null)[]>([]);
    const setInputRef = (index: number, el: unknown) => {
        inputRefs.value[index] = el as HTMLInputElement | null;
    };

    const updateDigitAt = (index: number) => {
        const digit = inputRefs.value[index]?.value.slice(-1) || "";
        const before = props.modelValue.slice(0, index);
        const after = props.modelValue.slice(index + 1);
        const newValue = (before.padEnd(index, "") + digit + after).slice(0, props.codeLength);
        emit("update:modelValue", newValue);
        if (digit && index < props.codeLength - 1) inputRefs.value[index + 1]?.focus();
    };

    const handleKeyDown = (index: number, event: KeyboardEvent) => {
        if (event.key === "Backspace" || event.key === "Delete") {
            event.preventDefault();
            const before = props.modelValue.slice(0, index);
            const after = props.modelValue.slice(index + 1);
            emit("update:modelValue", before + after);
            if (index > 0) inputRefs.value[index - 1]?.focus();
        }
    };

    const addDigit = (digit: string) => {
        if (props.modelValue.length >= props.codeLength) {
            return;
        }
        emit("update:modelValue", props.modelValue + digit);
    };

    const removeLastDigit = () => {
        emit("update:modelValue", props.modelValue.slice(0, -1));
    };

    const cancel = () => {
        emit("cancel");
    };

    const handlePaste = (event: ClipboardEvent) => {
        event.preventDefault();
        const raw = event.clipboardData?.getData("text") ?? "";
        const digits = raw.replace(/\D/g, "").slice(0, props.codeLength);
        if (!digits) return;
        emit("update:modelValue", digits);
        // focus the last filled input to allow quick corrections after paste
        nextTick(() => {
            inputRefs.value[digits.length - 1]?.focus();
        });
    };
</script>
